/**
 * A tab rendered by an older deployment posts server-action IDs the
 * current deployment does not have. Vercel Skew Protection routes those
 * calls back to the old deployment only within its max age (12 h on this
 * project), so a long-open tab eventually fails with Next's
 * `failed-to-find-server-action`. The action never ran, so a reload that
 * picks up the new build is always safe.
 */

import { unstable_isUnrecognizedActionError } from "next/navigation";
import { isServerActionNotFoundMessage } from "@/lib/virtualspace/scope-upload-error";
import { STALE_PAGE_MESSAGE } from "@/lib/virtualspace/stale-page-message";

// Client code only: server modules import the copy from `stale-page-message`.
export { STALE_PAGE_MESSAGE };

/** Header Next sets on the response when it has no action for the posted ID. */
const ACTION_NOT_FOUND_HEADER = "x-nextjs-action-not-found";

export function isStaleDeploymentError(error: unknown): boolean {
  if (unstable_isUnrecognizedActionError(error)) return true;
  const message =
    error instanceof Error ? error.message : typeof error === "string" ? error : "";
  return isServerActionNotFoundMessage(message);
}

export function isStaleActionResponse(response: Pick<Response, "headers">): boolean {
  return response.headers.get(ACTION_NOT_FOUND_HEADER) === "1";
}

/** What Next throws when a server action's answer is not an RSC payload. */
export const NEXT_UNEXPECTED_RESPONSE_MESSAGE =
  "An unexpected response was received from the server.";

/** Plain copy for that case. It does not claim a new release: it may not be one. */
export const UNEXPECTED_RESPONSE_MESSAGE =
  "We couldn't get a clear answer from the server, so that may not have gone through. Reload the page and check before trying again.";

const RSC_CONTENT_TYPE = "text/x-component";

function hasNextActionHeader(headers: HeadersInit | undefined | null): boolean {
  if (!headers) return false;
  try {
    return new Headers(headers).has("next-action");
  } catch {
    return false;
  }
}

/** The fetch was a server-action call (Next sends a `Next-Action` header). */
export function isServerActionRequest(
  input: RequestInfo | URL,
  init?: RequestInit,
): boolean {
  if (hasNextActionHeader(init?.headers)) return true;
  return typeof Request !== "undefined" && input instanceof Request
    ? hasNextActionHeader(input.headers)
    : false;
}

/**
 * A server-action answer Next cannot use: not an RSC payload, not a
 * redirect, and either a server error or an HTML page (a platform error
 * page, a proxy, a login wall). Next turns it into
 * "An unexpected response was received from the server." and the action
 * may or may not have run, so the page offers a reload.
 */
export function isUnexpectedActionResponse(
  response: Pick<Response, "headers" | "status" | "redirected">,
): boolean {
  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.startsWith(RSC_CONTENT_TYPE)) return false;
  if (response.redirected) return false;
  if (response.status >= 300 && response.status < 400) return false;
  if (response.headers.get("x-action-redirect")) return false;
  return response.status >= 500 || contentType.startsWith("text/html");
}

/**
 * Swap Next's missing-action and unexpected-response texts for plain copy;
 * leave anything else as is.
 */
export function readableStaleMessage<T>(message: T): T | string {
  if (typeof message !== "string") return message;
  if (isServerActionNotFoundMessage(message)) return STALE_PAGE_MESSAGE;
  if (message.trim() === NEXT_UNEXPECTED_RESPONSE_MESSAGE) {
    return UNEXPECTED_RESPONSE_MESSAGE;
  }
  return message;
}

export type StaleOrderSubmitContext = {
  isEditMode: boolean;
  /** The wizard autosaves a draft that a reload restores. */
  draftSaved: boolean;
};

export function staleOrderSubmitMessage({
  isEditMode,
  draftSaved,
}: StaleOrderSubmitContext): string {
  const lead = "We updated the app while this page was open, so";
  if (isEditMode) {
    return `${lead} your changes weren't saved. Reload the page, then make them again.`;
  }
  if (draftSaved) {
    return `${lead} your order wasn't sent. Reload the page — your order details are saved — and submit again.`;
  }
  return `${lead} your order wasn't sent. Reload the page and submit again.`;
}

/**
 * Same page, reopened on the new build. When a draft was saved, ask the
 * wizard to resume it straight away instead of showing the Resume banner.
 */
export function staleOrderReloadUrl(href: string, draftSaved: boolean): string {
  const url = new URL(href);
  if (draftSaved) {
    url.searchParams.set("resume", "1");
    url.searchParams.delete("fresh");
  }
  return url.toString();
}
